import { environment } from '../../environments/environment';
import { decodeJwtPayload } from '../auth/jwt.util';

/**
 * Marca de "volví de Agrology Connect" (`#connect=1`).
 *
 * ag-webapp redirige a `/companies#connect=1` después de que el usuario usó
 * Connect. La marca NO es secreta: solo avisa que hay que correr el apply
 * aunque la sesión venga restaurada. La identidad la resuelve api2 con el
 * `sub` del token Auth0 (no hay code ni vínculo por email).
 *
 * Por compatibilidad con la versión anterior de ag-webapp, `connect_code=<lo
 * que sea>` (en el fragmento o en el query) se trata igual que `connect=1`.
 *
 * Vive en sessionStorage (por pestaña) para sobrevivir al SSO por redirect de
 * top-level y a los reloads entre que el usuario llega desde Connect y termina
 * de loguearse. Se lee y se borra de la URL en un `APP_INITIALIZER`, antes de
 * que el router, el `AuthGuard` o el SSO toquen la URL.
 *
 * El router usa `PathLocationStrategy` (`useHash: false`), así que el
 * fragmento no forma parte de la ruta: quitar la marca de ahí no cambia la
 * navegación. Se conservan intactos los demás pares/anchors del fragmento y
 * del query (no se re-encodean).
 *
 * Funciones sueltas (sin DI) porque las usa el `APP_INITIALIZER` y
 * `LoginService.logout()/customLogout()`, que no pueden depender de
 * `ConnectApplyService` (este depende de `LoginService`).
 */

export const CONNECT_RETURN_PARAM = 'connect';
/** Parámetro de la versión con code de un solo uso (solo compat: se trata como `connect=1`). */
const LEGACY_CODE_PARAM = 'connect_code';
const CONNECT_PENDING_KEY = 'connect_pending';
/** Claves viejas (versión con code): solo se borran por limpieza. */
const LEGACY_STORAGE_KEYS = ['connect_code', 'connect_sent'];

/**
 * Separa `key` de una lista `a=1&b=2` (query sin `?` o fragmento sin `#`),
 * dejando el resto de las partes exactamente como estaban.
 * Devuelve `found: false` si el parámetro no está.
 */
function extractParam(raw: string, key: string): { found: boolean; value: string; rest: string } {
  const parts = raw ? raw.split('&') : [];
  let found = false;
  let value = '';
  const rest: string[] = [];

  for (const part of parts) {
    const eq = part.indexOf('=');
    const partKey = eq === -1 ? part : part.substring(0, eq);
    if (partKey === key) {
      if (!found) {
        found = true;
        try {
          value = decodeURIComponent((eq === -1 ? '' : part.substring(eq + 1)).replace(/\+/g, ' '));
        } catch (e) {
          value = '';
        }
      }
      continue;
    }
    rest.push(part);
  }

  return { found, value: value.trim(), rest: rest.filter(part => part !== '').join('&') };
}

/**
 * Lee `connect` (y, por compat, `connect_code`) del fragmento y del query de
 * la URL actual. Si aparece cualquiera, los quita todos con
 * `history.replaceState` conservando el resto y marca la vuelta pendiente si
 * `connect === '1'` o si vino `connect_code` (cualquier valor). Nunca tira: si
 * algo falla, la app arranca igual.
 */
export function captureConnectReturn(): void {
  try {
    const { pathname, search, hash } = window.location;

    const hashFlag = extractParam(hash.replace(/^#/, ''), CONNECT_RETURN_PARAM);
    const hashLegacy = extractParam(hashFlag.rest, LEGACY_CODE_PARAM);
    const queryFlag = extractParam(search.replace(/^\?/, ''), CONNECT_RETURN_PARAM);
    const queryLegacy = extractParam(queryFlag.rest, LEGACY_CODE_PARAM);

    const inHash = hashFlag.found || hashLegacy.found;
    const inQuery = queryFlag.found || queryLegacy.found;
    if (!inHash && !inQuery) return;

    const newSearch = inQuery ? (queryLegacy.rest ? `?${queryLegacy.rest}` : '') : search;
    const newHash = inHash ? (hashLegacy.rest ? `#${hashLegacy.rest}` : '') : hash;
    window.history.replaceState(window.history.state, '', pathname + newSearch + newHash);

    const pending = (hashFlag.found && hashFlag.value === '1')
      || (queryFlag.found && queryFlag.value === '1')
      || hashLegacy.found
      || queryLegacy.found;
    if (pending) sessionStorage.setItem(CONNECT_PENDING_KEY, '1');
  } catch (e) { /* URL/history/sessionStorage no disponibles: se ignora la marca */ }
}

/** El usuario volvió de Agrology Connect y el apply todavía no corrió. */
export function isConnectReturnPending(): boolean {
  try {
    return sessionStorage.getItem(CONNECT_PENDING_KEY) === '1';
  } catch (e) {
    return false;
  }
}

/** Borra la marca de vuelta pendiente (se consumió o se cerró sesión) y las claves viejas. */
export function clearConnectPending(): void {
  try {
    sessionStorage.removeItem(CONNECT_PENDING_KEY);
    LEGACY_STORAGE_KEYS.forEach(key => sessionStorage.removeItem(key));
  } catch (e) { /* sessionStorage no disponible */ }
}

/**
 * La sesión actual es de Auth0: el `access_token` es un JWT cuyo `iss` es
 * exactamente `https://${environment.auth0.domain}/` (mismo criterio que
 * `Auth0Service::isAuth0Token` de api2). Passport emite otro `iss`. Sin
 * `environment.auth0` siempre es legacy. No verifica la firma: solo decide si
 * vale la pena llamar al apply (api2 valida el token de verdad).
 */
export function isAuth0Session(accessToken: string | null | undefined): boolean {
  const domain = environment.auth0?.domain;
  if (!domain) return false;

  const payload = decodeJwtPayload(accessToken);
  return !!payload && payload.iss === `https://${domain}/`;
}

/**
 * Marca de "hubo un login real en esta pestaña" (`POST /agrology/connect/apply`
 * sin code corre UNA vez por login, no en cada arranque con sesión restaurada).
 *
 * La pone `LoginService` en cada login con sesión Auth0 (callback y popup de
 * Auth0 —incluido el SSO silencioso— y `authenticateWithRedirectedToken` con
 * token Auth0; nunca en login/registro legacy) con el id del usuario, y la
 * consume `ConnectApplyService` cuando termina la corrida. Vive en
 * sessionStorage para sobrevivir a un reload entre el login y la respuesta del
 * apply (p.ej. un F5 con el POST en vuelo): ese arranque todavía lo debe.
 */
const CONNECT_LOGIN_KEY = 'connect_login_pending';

/** Registra que `userId` acaba de loguearse en esta pestaña. */
export function markConnectLoginPending(userId: number): void {
  try {
    sessionStorage.setItem(CONNECT_LOGIN_KEY, String(userId));
  } catch (e) { /* sessionStorage no disponible */ }
}

/** Id del usuario con un login todavía sin apply, o `null`. */
export function getConnectLoginPendingUser(): number | null {
  try {
    const raw = sessionStorage.getItem(CONNECT_LOGIN_KEY);
    if (!raw) return null;
    const id = Number(raw);
    return Number.isFinite(id) ? id : null;
  } catch (e) {
    return null;
  }
}

/** El apply del login ya corrió (o el login es de otra sesión): se borra la marca. */
export function clearConnectLoginPending(): void {
  try {
    sessionStorage.removeItem(CONNECT_LOGIN_KEY);
  } catch (e) { /* sessionStorage no disponible */ }
}
